/**
 * Job Listing Module
 * Handles filter dropdown toggles, AJAX filtering, live debounced search, and pagination.
 */

export const initJobListing = () => {
	const blocks = document.querySelectorAll('.job-listing');
	if (!blocks.length) return;

	blocks.forEach((block) => {
		if (block.dataset.jlInitialized === 'true') return;
		block.dataset.jlInitialized = 'true';

		const dropdowns = block.querySelectorAll('.jl-filter-dropdown');
		const searchInput = block.querySelector('.jl-search-input');
		const itemsWrap = block.querySelector('.jl-items-wrap');
		const itemsList = block.querySelector('.jl-items-list');
		const paginationContainer = block.querySelector('.jl-pagination-container');

		const ajaxUrl = block.dataset.ajaxUrl || (typeof window !== 'undefined' && window.custom_ajax_object ? window.custom_ajax_object.ajax_url : '/wp-admin/admin-ajax.php');
		const nonce = block.dataset.nonce || (typeof window !== 'undefined' && window.custom_ajax_object ? window.custom_ajax_object.nonce : '');
		const postsPerPage = parseInt(block.dataset.postsPerPage, 10) || 10;

		let currentAbortController = null;
		let searchTimeout = null;
		let currentPage = 1;

		// 1. Setup Dropdown Toggles
		dropdowns.forEach((dropdown) => {
			const btn = dropdown.querySelector('.jl-filter-btn');
			const menu = dropdown.querySelector('.jl-filter-menu');

			if (!btn || !menu) return;

			btn.addEventListener('click', (e) => {
				e.preventDefault();
				e.stopPropagation();

				const isOpen = menu.classList.contains('is-open');

				// Close other dropdowns
				dropdowns.forEach((otherDropdown) => {
					if (otherDropdown !== dropdown) {
						const otherBtn = otherDropdown.querySelector('.jl-filter-btn');
						const otherMenu = otherDropdown.querySelector('.jl-filter-menu');
						if (otherBtn) {
							otherBtn.classList.remove('is-active');
							otherBtn.setAttribute('aria-expanded', 'false');
						}
						if (otherMenu) {
							otherMenu.classList.remove('is-open');
							otherMenu.setAttribute('aria-hidden', 'true');
						}
					}
				});

				// Toggle current dropdown
				if (isOpen) {
					btn.classList.remove('is-active');
					btn.setAttribute('aria-expanded', 'false');
					menu.classList.remove('is-open');
					menu.setAttribute('aria-hidden', 'true');
				} else {
					btn.classList.add('is-active');
					btn.setAttribute('aria-expanded', 'true');
					menu.classList.add('is-open');
					menu.setAttribute('aria-hidden', 'false');
				}
			});

			menu.addEventListener('click', (e) => {
				e.stopPropagation();
			});
		});

		// Close dropdowns on outside click
		document.addEventListener('click', () => {
			dropdowns.forEach((dropdown) => {
				const btn = dropdown.querySelector('.jl-filter-btn');
				const menu = dropdown.querySelector('.jl-filter-menu');
				if (btn && menu) {
					btn.classList.remove('is-active');
					btn.setAttribute('aria-expanded', 'false');
					menu.classList.remove('is-open');
					menu.setAttribute('aria-hidden', 'true');
				}
			});
		});

		// Close dropdowns on Escape key
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape') {
				dropdowns.forEach((dropdown) => {
					const btn = dropdown.querySelector('.jl-filter-btn');
					const menu = dropdown.querySelector('.jl-filter-menu');
					if (btn && menu) {
						btn.classList.remove('is-active');
						btn.setAttribute('aria-expanded', 'false');
						menu.classList.remove('is-open');
						menu.setAttribute('aria-hidden', 'true');
					}
				});
			}
		});

		// 2. Update Button Labels on Filter Selection
		const updateButtonLabels = () => {
			dropdowns.forEach((dropdown) => {
				const btn = dropdown.querySelector('.jl-filter-btn');
				const btnText = dropdown.querySelector('.jl-filter-btn-text');
				const defaultText = btnText?.getAttribute('data-default-text') || 'Filter';
				const checkedInputs = dropdown.querySelectorAll('input[type="checkbox"]:checked');

				if (btn && btnText) {
					if (checkedInputs.length === 1) {
						const labelSpan = checkedInputs[0].closest('label')?.querySelector('.jl-option-name');
						btnText.textContent = labelSpan ? labelSpan.textContent.trim() : defaultText;
						btn.classList.add('has-selection');
					} else if (checkedInputs.length > 1) {
						btnText.textContent = `${defaultText} (${checkedInputs.length})`;
						btn.classList.add('has-selection');
					} else {
						btnText.textContent = defaultText;
						btn.classList.remove('has-selection');
					}
				}
			});
		};

		// 3. Fetch Jobs via AJAX
		const fetchJobs = (page = 1, shouldScroll = false) => {
			currentPage = page;
			updateButtonLabels();

			// Abort any ongoing request
			if (currentAbortController) {
				currentAbortController.abort();
			}
			currentAbortController = new AbortController();

			// Collect selected filters
			const formData = new FormData();
			formData.append('action', 'cobalt_filter_jobs');
			formData.append('nonce', nonce);
			formData.append('paged', currentPage);
			formData.append('items_per_page', postsPerPage);

			// Collect departments
			block.querySelectorAll('.jl-filter-dropdown[data-filter="department"] input[type="checkbox"]:checked').forEach((input) => {
				formData.append('departments[]', input.value);
			});

			// Collect locations
			block.querySelectorAll('.jl-filter-dropdown[data-filter="location"] input[type="checkbox"]:checked').forEach((input) => {
				formData.append('locations[]', input.value);
			});

			// Collect offices
			block.querySelectorAll('.jl-filter-dropdown[data-filter="office"] input[type="checkbox"]:checked').forEach((input) => {
				formData.append('offices[]', input.value);
			});

			// Search query
			const query = searchInput ? searchInput.value.trim() : '';
			if (query) {
				formData.append('s', query);
			}

			// Add loading state
			if (itemsWrap) {
				itemsWrap.classList.add('is-loading');
			}
			if (itemsList) {
				itemsList.classList.add('is-loading');
			}

			fetch(ajaxUrl, {
				method: 'POST',
				body: formData,
				signal: currentAbortController.signal,
			})
				.then((response) => response.json())
				.then((result) => {
					if (itemsWrap) {
						itemsWrap.classList.remove('is-loading');
					}
					if (itemsList) {
						itemsList.classList.remove('is-loading');
					}

					if (result.success && result.data) {
						const { html, pagination_html, count } = result.data;

						if (itemsList) {
							itemsList.innerHTML = html || '';
						}

						if (paginationContainer) {
							paginationContainer.innerHTML = pagination_html || '';
						}

						// Re-bind pagination click events
						bindPaginationEvents();

						if (shouldScroll) {
							block.scrollIntoView({ behavior: 'smooth', block: 'start' });
						}
					}
				})
				.catch((err) => {
					if (err.name !== 'AbortError') {
						console.error('Job Listing AJAX filter error:', err);
						if (itemsWrap) {
							itemsWrap.classList.remove('is-loading');
						}
						if (itemsList) {
							itemsList.classList.remove('is-loading');
						}
					}
				});
		};

		// 4. Pagination Click Handlers
		const bindPaginationEvents = () => {
			if (!paginationContainer) return;

			paginationContainer.querySelectorAll('a.pagination-link:not(.disabled), a.page-numbers:not(.disabled)').forEach((link) => {
				link.addEventListener('click', (e) => {
					e.preventDefault();
					const targetPage = parseInt(link.dataset.page, 10);
					if (targetPage && targetPage !== currentPage) {
						fetchJobs(targetPage, true);
					}
				});
			});
		};

		// Initial bind for pagination links
		bindPaginationEvents();

		// 5. Checkbox Filter Change Event
		block.querySelectorAll('.jl-checkbox-label input[type="checkbox"]').forEach((input) => {
			input.addEventListener('change', () => {
				fetchJobs(1, false);
			});
		});

		// 6. Search Input with 300ms Debounce
		if (searchInput) {
			searchInput.addEventListener('input', () => {
				clearTimeout(searchTimeout);
				searchTimeout = setTimeout(() => {
					fetchJobs(1, false);
				}, 300);
			});

			searchInput.addEventListener('keydown', (e) => {
				if (e.key === 'Enter') {
					e.preventDefault();
					clearTimeout(searchTimeout);
					fetchJobs(1, false);
				}
			});
		}
	});
};

// Initialize
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initJobListing);
} else {
	initJobListing();
}

window.addEventListener('load', initJobListing);

// ACF Gutenberg block preview support
if (typeof window !== 'undefined' && window.acf) {
	window.acf.addAction('render_block_preview/type=job-listing', initJobListing);
}

export default initJobListing;
